import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Tag,
  Tabs,
  Drawer,
  Form,
  Input,
  Select,
  Alert,
  Spin,
  message,
  Typography,
  Tooltip,
  Space,
  Badge,
  Segmented,
} from "antd";
import {
  SafetyCertificateOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  RobotOutlined,
  EyeOutlined,
  AuditOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Text } = Typography;
const { TextArea } = Input;

const ID_STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "Verified", label: "Verified" },
  { value: "Failed", label: "Failed" },
];

const BGV_STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "Yes", label: "Verified" },
  { value: "No", label: "Failed" },
];

const idStatusTag = (status) => {
  if (status === "Verified")
    return <Tag icon={<CheckCircleOutlined />} color="success">Verified</Tag>;
  if (status === "Failed")
    return <Tag icon={<CloseCircleOutlined />} color="error">Failed</Tag>;
  return <Tag icon={<ClockCircleOutlined />} color="warning">Pending</Tag>;
};

const bgvStatusTag = (val) => {
  if (val === "Yes") return <Tag icon={<CheckCircleOutlined />} color="success">Verified</Tag>;
  if (val === "No") return <Tag icon={<CloseCircleOutlined />} color="error">Failed</Tag>;
  return <Tag icon={<ClockCircleOutlined />} color="warning">Pending</Tag>;
};

const IDVerification = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("ID");
  const [activeTab, setActiveTab] = useState("Pending");
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(null);
  const [extractedData, setExtractedData] = useState({});
  const [form] = Form.useForm();

  const fetchList = useCallback(async (section, tab) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      let params = "";
      if (section === "ID") {
        params = `?idVerificationStatus=${tab}`;
      } else {
        const bgvMap = { Pending: "Pending", Verified: "Yes", Failed: "No" };
        params = `?backgroundVerified=${bgvMap[tab] || "Pending"}`;
      }
      const response = await axios.get(`${API_CONST.GET_VERIFICATION_LIST}${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTalents(response.data.talents || []);
    } catch (error) {
      console.error("Error fetching verification list:", error);
      message.error("Failed to load verification list");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList(activeSection, activeTab);
  }, [activeSection, activeTab, fetchList]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    setActiveTab("Pending");
    setTalents([]);
  };

  const openDrawer = (record) => {
    setSelectedTalent(record);
    setExtractedData({});
    form.setFieldsValue({
      idVerificationStatus: record.idVerificationStatus || "Pending",
      backgroundVerified: record.backgroundVerified || "Pending",
      aadhaarNumber: record.aadhaarNumber || "",
      panNumber: record.panNumber || "",
      passportNumber: record.passportNumber || "",
      idVerificationNotes: record.idVerificationNotes || "",
    });
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedTalent(null);
    setExtractedData({});
    form.resetFields();
  };

  const handleExtract = async (docType) => {
    if (!selectedTalent) return;
    const docUrl = {
      aadhaar: selectedTalent.aadhaarUrl,
      pan: selectedTalent.panUrl,
      degree: selectedTalent.degreeUrl,
    }[docType];
    if (!docUrl) {
      message.warning(`No ${docType} document uploaded for this talent`);
      return;
    }
    setExtracting(docType);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.EXTRACT_TALENT_DOCUMENT,
        { talentId: selectedTalent.id, documentType: docType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const extracted = response.data.extracted;

      // Normalise name in case backend returns an object (safety net for older model responses)
      if (extracted?.name && typeof extracted.name === "object") {
        extracted.name = Object.values(extracted.name).filter(Boolean).join(" / ");
      }

      setExtractedData((prev) => ({ ...prev, [docType]: extracted }));

      // Pre-fill the form field with the extracted ID number
      let idField = null;
      if (extracted?.idNumber) {
        if (docType === "aadhaar") { form.setFieldValue("aadhaarNumber", extracted.idNumber); idField = "aadhaarNumber"; }
        else if (docType === "pan") { form.setFieldValue("panNumber", extracted.idNumber); idField = "panNumber"; }
        else if (docType === "passport") { form.setFieldValue("passportNumber", extracted.idNumber); idField = "passportNumber"; }
      }

      // Auto-save the extracted ID number to DB immediately so it isn't lost
      // if the admin closes the drawer without clicking "Save Verification".
      if (idField && extracted?.idNumber) {
        try {
          await axios.patch(
            `${API_CONST.VERIFY_TALENT}/${selectedTalent.id}`,
            { [idField]: extracted.idNumber },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          // Update local selectedTalent state so re-opening the drawer shows saved value
          setSelectedTalent((prev) => ({ ...prev, [idField]: extracted.idNumber }));
          message.success(`${docType.toUpperCase()} extracted & auto-saved — edit the number below if needed`);
        } catch {
          message.success(`Data extracted from ${docType} — click Save to persist`);
        }
      } else {
        message.success(`Data extracted from ${docType} document`);
      }
    } catch (error) {
      message.error(error.response?.data?.message || `Failed to extract ${docType} data`);
    } finally {
      setExtracting(null);
    }
  };

  const handleSave = async () => {
    if (!selectedTalent) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      const token = localStorage.getItem("authToken");
      await axios.patch(
        `${API_CONST.VERIFY_TALENT}/${selectedTalent.id}`,
        {
          idVerificationStatus: values.idVerificationStatus,
          backgroundVerified: values.backgroundVerified,
          aadhaarNumber: values.aadhaarNumber || undefined,
          panNumber: values.panNumber || undefined,
          passportNumber: values.passportNumber || undefined,
          idVerificationNotes: values.idVerificationNotes || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Verification updated successfully");
      closeDrawer();
      fetchList(activeSection, activeTab);
    } catch (error) {
      if (error.errorFields) return;
      message.error(error.response?.data?.message || "Failed to update verification");
    } finally {
      setSaving(false);
    }
  };

  const hasDoc = (talent, docType) => {
    const map = {
      aadhaar: talent.aadhaarUrl,
      pan: talent.panUrl,
      degree: talent.degreeUrl,
      resume: talent.resumeUrl,
    };
    return !!map[docType];
  };

  const tabItems = [
    {
      key: "Pending",
      label: (
        <span>
          <ClockCircleOutlined style={{ color: "#faad14" }} /> Pending
          {activeTab === "Pending" && (
            <Badge count={talents.length} style={{ marginLeft: 6, backgroundColor: "#faad14" }} />
          )}
        </span>
      ),
    },
    {
      key: "Verified",
      label: (
        <span>
          <CheckCircleOutlined style={{ color: "#52c41a" }} /> Verified
          {activeTab === "Verified" && (
            <Badge count={talents.length} style={{ marginLeft: 6, backgroundColor: "#52c41a" }} />
          )}
        </span>
      ),
    },
    {
      key: "Failed",
      label: (
        <span>
          <CloseCircleOutlined style={{ color: "#ff4d4f" }} /> Failed
          {activeTab === "Failed" && (
            <Badge count={talents.length} style={{ marginLeft: 6, backgroundColor: "#ff4d4f" }} />
          )}
        </span>
      ),
    },
  ];

  const sharedNameCol = {
    title: "Talent Name",
    dataIndex: "name",
    key: "name",
    width: 160,
    render: (text, record) => (
      <div>
        <div style={{ fontWeight: 600, color: "#014c75" }}>{text}</div>
        <div style={{ fontSize: 12, color: "#888" }}>{record.id}</div>
      </div>
    ),
  };

  const sharedRoleCol = {
    title: "Role",
    dataIndex: "role",
    key: "role",
    width: 140,
    ellipsis: true,
  };

  const sharedOrgCol = {
    title: "Organization",
    dataIndex: "partnerOrganization",
    key: "partnerOrganization",
    width: 150,
    ellipsis: true,
  };

  const actionCol = {
    title: "Action",
    key: "action",
    width: 120,
    fixed: "right",
    render: (_, record) => (
      <Space>
        <Tooltip title="Review & Verify">
          <Button
            type="primary"
            size="small"
            icon={<SafetyCertificateOutlined />}
            style={{ background: "#014c75", borderColor: "#014c75" }}
            onClick={() => openDrawer(record)}
          >
            Review
          </Button>
        </Tooltip>
        <Tooltip title="View Documents">
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() =>
              navigate(`/admin/talent-profiles/documents/${record.id}`, {
                state: { from: "id-verification" },
              })
            }
          />
        </Tooltip>
      </Space>
    ),
  };

  const idColumns = [
    sharedNameCol,
    sharedRoleCol,
    sharedOrgCol,
    {
      title: "Documents",
      key: "docs",
      width: 180,
      render: (_, record) => (
        <Space size={4} wrap>
          <Tag color={record.resumeUrl ? "blue" : "default"}>Resume</Tag>
          <Tag color={record.aadhaarUrl ? "blue" : "default"}>Aadhaar</Tag>
          <Tag color={record.panUrl ? "blue" : "default"}>PAN</Tag>
          <Tag color={record.degreeUrl ? "blue" : "default"}>Degree</Tag>
        </Space>
      ),
    },
    {
      title: "ID Status",
      dataIndex: "idVerificationStatus",
      key: "idVerificationStatus",
      width: 120,
      render: (status) => idStatusTag(status),
    },
    {
      title: "ID Numbers",
      key: "idNums",
      width: 180,
      render: (_, record) => (
        <div style={{ fontSize: 12 }}>
          {record.aadhaarNumber && <div><Text type="secondary">Aadhaar: </Text>{record.aadhaarNumber}</div>}
          {record.panNumber && <div><Text type="secondary">PAN: </Text>{record.panNumber}</div>}
          {record.passportNumber && <div><Text type="secondary">Passport: </Text>{record.passportNumber}</div>}
          {!record.aadhaarNumber && !record.panNumber && !record.passportNumber && (
            <Text type="secondary">—</Text>
          )}
        </div>
      ),
    },
    {
      title: "Verified On",
      dataIndex: "verifiedAt",
      key: "verifiedAt",
      width: 110,
      render: (date) => date ? new Date(date).toLocaleDateString("en-IN") : "—",
    },
    actionCol,
  ];

  const bgvColumns = [
    sharedNameCol,
    sharedRoleCol,
    sharedOrgCol,
    {
      title: "Documents",
      key: "docs",
      width: 180,
      render: (_, record) => (
        <Space size={4} wrap>
          <Tag color={record.resumeUrl ? "blue" : "default"}>Resume</Tag>
          <Tag color={record.aadhaarUrl ? "blue" : "default"}>Aadhaar</Tag>
          <Tag color={record.panUrl ? "blue" : "default"}>PAN</Tag>
          <Tag color={record.degreeUrl ? "blue" : "default"}>Degree</Tag>
        </Space>
      ),
    },
    {
      title: "BG Check Status",
      dataIndex: "backgroundVerified",
      key: "backgroundVerified",
      width: 140,
      render: (val) => bgvStatusTag(val),
    },
    {
      title: "ID Verification",
      dataIndex: "idVerificationStatus",
      key: "idVerificationStatus",
      width: 130,
      render: (status) => idStatusTag(status),
    },
    {
      title: "Verification Notes",
      dataIndex: "idVerificationNotes",
      key: "idVerificationNotes",
      width: 180,
      ellipsis: true,
      render: (notes) => notes ? <Text type="secondary" ellipsis>{notes}</Text> : "—",
    },
    actionCol,
  ];

  const activeColumns = activeSection === "ID" ? idColumns : bgvColumns;
  const emptyLabel = activeSection === "ID" ? "ID verification" : "background verification";

  return (
    <div style={{ padding: "0 0 24px" }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#014c75", margin: 0 }}>
          Verification Management
        </h1>
        <p style={{ color: "#666", marginTop: 4, marginBottom: 0 }}>
          Review and manage identity and background verification for talent profiles
        </p>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Segmented
          value={activeSection}
          onChange={handleSectionChange}
          options={[
            {
              label: (
                <span>
                  <SafetyCertificateOutlined style={{ marginRight: 6 }} />
                  ID Verification
                </span>
              ),
              value: "ID",
            },
            {
              label: (
                <span>
                  <AuditOutlined style={{ marginRight: 6 }} />
                  BG Verification
                </span>
              ),
              value: "BGV",
            },
          ]}
          style={{ fontSize: 14 }}
        />
      </div>

      <Tabs
        activeKey={activeTab}
        items={tabItems}
        onChange={(key) => {
          setActiveTab(key);
          setTalents([]);
        }}
        style={{ marginBottom: 16 }}
      />

      <Table
        columns={activeColumns}
        dataSource={talents}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1200 }}
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
        locale={{
          emptyText: (
            <div style={{ padding: "40px 0", textAlign: "center", color: "#888" }}>
              <SafetyCertificateOutlined style={{ fontSize: 40, marginBottom: 12, display: "block" }} />
              No {activeTab.toLowerCase()} {emptyLabel} records found
            </div>
          ),
        }}
      />

      <Drawer
        title={
          <span>
            <SafetyCertificateOutlined style={{ marginRight: 8, color: "#014c75" }} />
            Review Verification — {selectedTalent?.name}
          </span>
        }
        open={drawerVisible}
        onClose={closeDrawer}
        width={520}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button
              type="primary"
              loading={saving}
              style={{ background: "#014c75", borderColor: "#014c75" }}
              onClick={handleSave}
            >
              Save Verification
            </Button>
          </div>
        }
      >
        {selectedTalent && (
          <>
            <div
              style={{
                background: "#f8f9fd",
                borderRadius: 8,
                padding: "12px 16px",
                marginBottom: 20,
                border: "1px solid #e8e8e8",
              }}
            >
              <div style={{ fontWeight: 600, color: "#014c75", fontSize: 16 }}>{selectedTalent.name}</div>
              <div style={{ color: "#666", fontSize: 13, marginTop: 2 }}>
                {selectedTalent.role} &bull; {selectedTalent.partnerOrganization}
              </div>
              <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  { type: "aadhaar", label: "Aadhaar" },
                  { type: "pan", label: "PAN" },
                  { type: "degree", label: "Degree" },
                  { type: "resume", label: "Resume" },
                ].map((doc) => (
                  <Tag
                    key={doc.type}
                    color={hasDoc(selectedTalent, doc.type) ? "blue" : "default"}
                    icon={<FileTextOutlined />}
                  >
                    {doc.label}
                  </Tag>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <Text strong style={{ fontSize: 13, color: "#333", display: "block", marginBottom: 10 }}>
                AI OCR Extraction
              </Text>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["aadhaar", "pan", "degree"].map((docType) => (
                  <Button
                    key={docType}
                    size="small"
                    icon={
                      extracting === docType ? (
                        <Spin indicator={<LoadingOutlined spin />} size="small" />
                      ) : (
                        <RobotOutlined />
                      )
                    }
                    disabled={!hasDoc(selectedTalent, docType) || extracting === docType}
                    onClick={() => handleExtract(docType)}
                    style={
                      hasDoc(selectedTalent, docType)
                        ? { borderColor: "#1677ff", color: "#1677ff" }
                        : {}
                    }
                  >
                    {extractedData[docType] ? `Re-extract ${docType}` : `Extract ${docType}`}
                  </Button>
                ))}
              </div>

              {Object.entries(extractedData).map(([docType, data]) =>
                data ? (
                  <Alert
                    key={docType}
                    style={{ marginTop: 10, fontSize: 12 }}
                    type="info"
                    showIcon
                    message={
                      <div>
                        <strong>{docType.toUpperCase()} OCR Result</strong>
                        {data.name && <div>Name: {data.name}</div>}
                        {data.idNumber && <div>ID: {data.idNumber}</div>}
                        {data.dateOfBirth && <div>DOB: {data.dateOfBirth}</div>}
                        {data.extractionConfidence && (
                          <Tag
                            color={
                              data.extractionConfidence === "High"
                                ? "success"
                                : data.extractionConfidence === "Medium"
                                ? "warning"
                                : "error"
                            }
                            style={{ marginTop: 4 }}
                          >
                            {data.extractionConfidence} Confidence
                          </Tag>
                        )}
                      </div>
                    }
                  />
                ) : null
              )}
            </div>

            <Form form={form} layout="vertical">
              <Form.Item
                name="idVerificationStatus"
                label="ID Verification Status"
                rules={[{ required: true, message: "Please select a status" }]}
              >
                <Select options={ID_STATUS_OPTIONS} />
              </Form.Item>

              <Form.Item name="backgroundVerified" label="Background Verification Status">
                <Select options={BGV_STATUS_OPTIONS} />
              </Form.Item>

              <Form.Item name="aadhaarNumber" label="Aadhaar Number">
                <Input placeholder="e.g. 1234 5678 9012" maxLength={20} />
              </Form.Item>

              <Form.Item name="panNumber" label="PAN Number">
                <Input placeholder="e.g. ABCDE1234F" maxLength={20} />
              </Form.Item>

              <Form.Item name="passportNumber" label="Passport Number">
                <Input placeholder="e.g. A1234567" maxLength={40} />
              </Form.Item>

              <Form.Item name="idVerificationNotes" label="Verification Notes">
                <TextArea
                  rows={3}
                  placeholder="Add notes about the verification decision..."
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default IDVerification;
